import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Box, Divider, Stack, MenuItem, ListItemIcon } from '@mui/material';
import mixpanel from 'mixpanel-browser';

import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useResponsive from 'src/hooks/useResponsive';
import useMerchant from 'src/hooks/useMerchant';
import useCarbonUser from 'src/hooks/useCarbonUser';
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { AccountBadge } from '../../../components/subscription/AccountBadge';
import MerchantSwitcher from './MerchantSwitcher';
import { IconChevronDown } from 'src/libraries/icons';
import { AppTypography } from 'src/components';

//
//

const MENU_OPTIONS = [
  {
    label: 'Account',
    linkTo: PATH_DASHBOARD.account.accountDetails,
    icon: <AccountBadge />
  },
  {
    label: 'Packages',
    linkTo: PATH_DASHBOARD.account.plans,
  },
  {
    label: 'User management',
    linkTo: PATH_DASHBOARD.account.userManagement
  }
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');

  const { getMerchantUsers } = useMerchant();

  const { user, logout } = useAuth();
  const { carbonUser } = useCarbonUser();
  const { merchant } = useMerchant();

  const isMountedRef = useIsMountedRef();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    if(carbonUser) {
      getMerchantUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carbonUser?.merchantUsers?.length]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      //removes currently set data; in case a different user logs in next
      mixpanel.reset();
      if (isMountedRef.current) {
        handleClose();
      }
      window.location.reload();
    } catch (error) {
      console.error('Logout', error);
    }
  };

  const onChangeMerchant = (merchantId: string) => {
    localStorage.setItem('storedMerchantId', JSON.stringify(merchantId));
    navigate(PATH_DASHBOARD.root, { replace: true });
    window.location.reload();
    handleClose();
  };

  return (
    <>
      <Stack spacing={2} sx={{ ml: '8px !important' }} direction={'row'} alignItems={'center'} onClick={handleOpen}>
        {isDesktop && 
          <IconChevronDown color="black" size={18} />
        }
        <IconButtonAnimate
          sx={{
            p: 0,
            ...(open && {
              '&:before': {
                zIndex: 1,
                content: "''",
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                position: 'absolute',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
              },
            }),
          }}
        >

          <MyAvatar />
        </IconButtonAnimate>
      </Stack>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          overflow: {
            xs: 'scroll',
            sm: 'initial'
          },
          boxShadow: theme => `${theme.customShadows.card} !important`,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
            minHeight: 36,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <AppTypography variant='h6' noWrap>
            {user?.name}
          </AppTypography>
          <AppTypography sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </AppTypography>
        </Box>

        <MerchantSwitcher onChange={onChangeMerchant} />

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ py: 1 }}>
          {merchant && merchant.name && <AppTypography variant="h6" sx={{ p: '0.6rem 1rem' }}>
            {merchant.name}
          </AppTypography>}

          <AppTypography variant='system' sx={{ px: 2 }} color='text.secondary'>{merchant?.code}</AppTypography>

          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              <AppTypography>{option.label}</AppTypography>
              {option?.icon &&
                <ListItemIcon sx={{ ml: 1 }}>
                  {option.icon}
                </ListItemIcon>
                }
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem component={RouterLink} to={PATH_DASHBOARD.settings.business} sx={{ py: 1 }}>
          <AppTypography fontWeight='bold'>Settings</AppTypography>
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed', m: '0 !important' }} />

        <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
          <AppTypography>Logout</AppTypography>
        </MenuItem>
      </MenuPopover>
    </>
  );
}
